import type { HeldRole } from '@repo/domain';
import { isApplicationMode } from '@repo/env-variables';
import { createStore } from '@repo/zustand';

import { ROLE_PERMISSIONS } from '@features/auth/constants/rolePermissions';
import { holdsPermission } from '@features/auth/utils/holdsPermission';

/**
 * Roles of the signed-in person, read from the access token by `@repo/repo-parkapp`.
 *
 * `heldRoles` is `undefined` while the roles are not known — right after a page reload the first
 * access token is still on its way — which differs from an empty list, meaning no roles at all.
 * Kept in memory only: a persisted copy could outlive the session the roles belong to.
 *
 * The roles decide what the UI offers, never what the backend allows.
 */
type State = {
  heldRoles: HeldRole[] | undefined;

  setHeldRoles: (heldRoles: HeldRole[] | undefined) => void;
  reset: () => void;
};

export const useHeldRolesStore = createStore<State>({
  name: 'HeldRolesStore',
  isDev: isApplicationMode('dev'),
})((set) => ({
  heldRoles: undefined,

  setHeldRoles: (heldRoles: HeldRole[] | undefined) => {
    set({ heldRoles });
  },

  reset: () => {
    set({ heldRoles: undefined });
  },
}));

/**
 * Whether the person may switch to logging in with a password: known roles, none of them a system
 * role. The backend makes every system role log in with an email code, so while the roles are
 * still unknown the choice is not offered either.
 */
export const selectCanChoosePasswordLogin = (state: State): boolean =>
  state.heldRoles !== undefined && !state.heldRoles.some((heldRole) => heldRole.scopeType === 'system');

/**
 * Whether the person may see the support phone number: any system or organization role, or
 * `parking.owner`. Hidden while the roles are still unknown.
 */
export const selectCanSeeSupportPhone = (state: State): boolean =>
  state.heldRoles?.some(
    (heldRole) =>
      heldRole.scopeType === 'system' || heldRole.scopeType === 'organization' || heldRole.roleKey === 'parking.owner'
  ) ?? false;

/**
 * Whether the person may check the vehicles on their parkings and report an unauthorized use: a role
 * held on a parking or an organization that carries `unauthorizedUseCase.create`. System roles do not
 * count — a ParkApp controller reaches the control from its own entry. Not offered while the roles are
 * still unknown.
 */
export const selectCanControlParkingVehicles = (state: State): boolean =>
  holdsPermission({
    heldRoles: state.heldRoles?.filter((heldRole) => heldRole.scopeType !== 'system'),
    permission: 'unauthorizedUseCase.create',
    target: undefined,
    rolePermissions: ROLE_PERMISSIONS,
  });

/**
 * Whether the person is a ParkApp controller: `system.controller`. `undefined` while the roles are
 * still unknown, so neither the controller entries nor the invitation to become one are offered yet.
 */
export const selectIsSystemController = (state: State): boolean | undefined =>
  state.heldRoles?.some((heldRole) => heldRole.roleKey === 'system.controller');
